import React, { useState, useMemo } from 'react';
import { useCRM } from '../context/CRMContext';
import { usePlaybook } from '../context/PlaybookContext';
import SafeIcon from '../common/SafeIcon';
import SafeChart from '../components/common/SafeChart';
import { motion } from 'framer-motion';
import { formatDate } from '../utils/helpers';

const AICoach = () => {
  const { deals, activities, currentUser } = useCRM();
  const { coachEvents, runAiNextBestAction, learningModules } = usePlaybook();
  const [analyzing, setAnalyzing] = useState(false);

  // --- ANALYSIS LOGIC ---
  const insights = useMemo(() => {
    const list = [];
    const now = new Date();
    
    // 1. Stalled Deals (Avtaler uten fremdrift)
    // Finner avtaler eid av brukeren som er åpne
    const stalledDeals = deals.filter(d => 
      d.status === 'Åpen' && 
      d.owner_user_id === currentUser.user_id
    );
    
    // For demo: Hvis ingen aktivitet siste 7 dager (simulert ved å ta de 2 første)
    stalledDeals.slice(0, 2).forEach(d => {
       list.push({
         id: `stalled-${d.deal_id}`,
         type: 'Risk',
         title: `Risiko: ${d.title}`,
         message: 'Ingen aktivitet registrert siste uke. Foreslår å sende en "breakup" mail eller ringe for å avklare status.',
         action: 'Gå til Deal',
         link: `/pipeline` // Kunne vært direkte til deal
       });
    });

    // 2. Performance Check
    const myActivities = activities.filter(a => a.user_id === currentUser.user_id && a.status === 'Gjennomført');
    // Enkel sjekk: Har du gjort noe denne uken?
    if (myActivities.length < 5) {
      list.push({
        id: 'low-activity',
        type: 'Improvement',
        title: 'Lav aktivitet denne uken',
        message: 'Du ligger under målet på 15 aktiviteter. Book 3 møter i dag for å hente inn forspranget.',
        action: 'Gå til Aktiviteter',
        link: '/activities'
      });
    }

    return list;
  }, [deals, activities, currentUser]);

  const handleRunAnalysis = async () => {
    setAnalyzing(true);
    // Simulere en dypere analyse som tar litt tid
    await new Promise(r => setTimeout(r, 2000));
    
    // Trigger en faktisk AI-hendelse i systemet (hvis vi har et prosjekt å knytte det til)
    // Her bruker vi en "general" kontekst hvis ingen prosjekt-ID er tilgjengelig
    // I en ekte app ville vi sendt med ID-en til det viktigste prosjektet
    await runAiNextBestAction('general-context');
    
    setAnalyzing(false);
  };

  // Chart Data: Ferdighetsanalyse (Mock)
  const performanceChart = {
    title: { text: 'Dine ferdigheter vs Teamet', left: 'center', top: 0, textStyle: { fontSize: 14 } },
    radar: {
      indicator: [
        { name: 'Closing', max: 100 },
        { name: 'Discovery', max: 100 },
        { name: 'Outreach', max: 100 },
        { name: 'Forhandling', max: 100 },
        { name: 'Produktkunnskap', max: 100 }
      ],
      radius: '60%',
      center: ['50%', '55%']
    },
    series: [{
      type: 'radar',
      data: [
        {
          value: [80, 60, 90, 70, 85],
          name: 'Deg',
          itemStyle: { color: '#3b82f6' },
          areaStyle: { opacity: 0.2 }
        },
        {
          value: [70, 75, 80, 65, 70],
          name: 'Team Snitt',
          itemStyle: { color: '#94a3b8' },
          lineStyle: { type: 'dashed' }
        }
      ]
    }]
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8 animate-in fade-in">
      {/* Header */}
      <div className="flex justify-between items-center border-b border-slate-200 pb-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-[10px] font-black uppercase tracking-widest mb-2 border border-purple-100">
            <SafeIcon icon="🤖" className="w-3 h-3" /> AI Performance Coach
          </div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Din Salgscoach</h1>
          <p className="text-slate-500 font-medium mt-1">Personlig veiledning basert på dine data og adferd.</p>
        </div>
        <button 
          onClick={handleRunAnalysis} 
          disabled={analyzing}
          className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg hover:scale-105 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {analyzing ? <SafeIcon icon="🔄" className="animate-spin" /> : <SafeIcon icon="✨" />} 
          {analyzing ? 'Analyserer...' : 'Kjør Full Analyse'}
        </button>
      </div>

      {/* Top Insights Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Insight Card 1: Focus Area */}
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-[2rem] p-8 text-white shadow-xl relative overflow-hidden flex flex-col justify-between min-h-[240px]">
           <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16"></div>
           <div>
             <h3 className="text-lg font-black mb-4 relative z-10 flex items-center gap-2">
               <SafeIcon icon="🎯" /> Fokus i dag
             </h3>
             <div className="space-y-4 relative z-10">
               <div className="flex items-start gap-3 bg-white/10 p-4 rounded-xl backdrop-blur-sm border border-white/10">
                 <div>
                   <p className="font-bold text-sm mb-1">Follow-up Friday</p>
                   <p className="text-xs opacity-80 leading-relaxed">Fredager er best for å lukke åpne loops. Gå gjennom dine 3 eldste åpne deals og send en statusoppdatering.</p>
                 </div>
               </div>
             </div>
           </div>
           <button className="mt-4 w-full py-2 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors backdrop-blur-sm">
             Se anbefalte deals
           </button>
        </div>

        {/* Insight Card 2: Actionable Alerts */}
        <div className="bg-white rounded-[2rem] p-8 border border-slate-200 shadow-sm flex flex-col min-h-[240px]">
           <h3 className="text-lg font-black text-slate-900 mb-4 flex items-center gap-2">
             <SafeIcon icon="⚠️" className="text-amber-500" /> Handlingspunkter
           </h3>
           <div className="flex-1 overflow-y-auto custom-scrollbar pr-1">
             {insights.length > 0 ? (
               <div className="space-y-3">
                 {insights.map(insight => (
                   <div key={insight.id} className="p-3 bg-slate-50 rounded-xl border border-slate-100 hover:border-amber-200 transition-colors">
                     <p className="font-bold text-sm text-slate-800 mb-1">{insight.title}</p>
                     <p className="text-xs text-slate-500 leading-relaxed">{insight.message}</p>
                   </div>
                 ))}
               </div>
             ) : (
               <div className="h-full flex flex-col items-center justify-center text-center opacity-60">
                 <SafeIcon icon="✅" className="text-4xl text-emerald-500 mb-3" />
                 <p className="font-bold text-slate-900">Alt ser bra ut!</p>
                 <p className="text-xs text-slate-500">Ingen kritiske avvik funnet.</p>
               </div>
             )}
           </div>
        </div>

        {/* Insight Card 3: Skills Radar */}
        <div className="bg-white rounded-[2rem] p-6 border border-slate-200 shadow-sm min-h-[240px] flex flex-col">
           <SafeChart option={performanceChart} height="200px" style={{flex: 1}} />
           <div className="text-center mt-2">
             <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Basert på siste 30 dager</p>
           </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: Feed */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-black text-slate-900">Coach Feed</h3>
            <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-lg">Siste oppdateringer</span>
          </div>
          
          {coachEvents.length > 0 ? (
            <div className="space-y-4">
              {coachEvents.map((event, idx) => (
                <motion.div 
                  initial={{opacity: 0, y: 10}} 
                  animate={{opacity: 1, y: 0}} 
                  transition={{delay: idx * 0.1}}
                  key={event.id} 
                  className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex gap-4 hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center shrink-0 border border-indigo-100">
                    <SafeIcon icon="🤖" className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-1">
                      <h4 className="font-bold text-slate-900 text-sm">Tips fra AI</h4>
                      <span className="text-xs text-slate-400 font-mono">{formatDate(event.created_at)}</span>
                    </div>
                    <p className="text-slate-600 text-sm leading-relaxed">{event.message}</p>
                    <div className="mt-3 flex gap-2">
                      <button className="text-[10px] font-bold text-slate-400 hover:text-indigo-600 flex items-center gap-1 transition-colors">
                        <SafeIcon icon="👍" className="w-3 h-3" /> Nyttig
                      </button>
                      <button className="text-[10px] font-bold text-slate-400 hover:text-slate-600 flex items-center gap-1 transition-colors ml-2">
                        <SafeIcon icon="👎" className="w-3 h-3" /> Ikke relevant
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl">
              <SafeIcon icon="💤" className="w-10 h-10 text-slate-300 mb-3 block mx-auto" />
              <p className="text-slate-500 font-bold text-sm">Ingen hendelser i feeden enda.</p>
              <p className="text-slate-400 text-xs mt-1">Trykk på "Kjør Full Analyse" for å starte.</p>
            </div>
          )}
        </div>

        {/* Right: Learning & Resources */}
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-black text-slate-900">Anbefalt Læring</h3>
            <button className="text-xs font-bold text-primary-600 hover:underline">Se alle</button>
          </div>
          
          <div className="space-y-4">
            {learningModules.length > 0 ? learningModules.slice(0, 3).map(module => (
              <div key={module.plm_id} className="p-5 bg-white border border-slate-200 rounded-2xl hover:shadow-md hover:border-primary-200 transition-all cursor-pointer group">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-blue-50 text-blue-600 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-colors">
                    <SafeIcon icon="🎓" className="w-4 h-4" />
                  </div>
                  <h4 className="font-bold text-sm text-slate-900">{module.title}</h4>
                </div>
                <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed mb-3">{module.learning_objectives}</p>
                <div className="flex items-center justify-between pt-3 border-t border-slate-50">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{module.content_source}</span>
                  <SafeIcon icon="➡️" className="w-3 h-3 text-slate-300 group-hover:text-primary-600 group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            )) : (
              <div className="p-6 bg-slate-50 rounded-2xl border border-slate-200 text-center">
                <p className="text-slate-400 text-xs font-bold">Ingen moduler tilgjengelig.</p>
                <p className="text-slate-400 text-[10px] mt-1">Opprett moduler i Playbook-fanen på et prosjekt.</p>
              </div>
            )}
            
            {/* Static Recommendation fallback if empty */}
            {learningModules.length === 0 && (
              <div className="p-5 bg-white border border-slate-200 rounded-2xl opacity-75">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xl">📘</span>
                  <h4 className="font-bold text-sm text-slate-900">Sales Playbook 101</h4>
                </div>
                <p className="text-xs text-slate-500">Grunnleggende innføring i vår salgsmetodikk.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AICoach;